import { NextApiRequest } from "next";
import React, { useEffect } from "react";

import sanity from "/lib/sanity/client";

import Seo from "/utils/seo";

import { Section, Block } from "/components/Layout";
import CmsBlock from "/components/CmsBlock";
import Footer from "/components/Footer";

import Stepper from "/components/BikeOrder/Stepper";

import { stripe } from "/lib/stripe";
import handleServerSidePropsError from "/utils/handleServerSidePropsError";
import { trackPixel } from "/utils/metaPixel";

import bikeModelsQ, { BikeModelsQ, createBikeModelsQuery } from "/lib/sanity/queries/bikeModels";

import * as Sentry from "@sentry/nextjs";
import { NextApiRequestQuery } from "next/dist/server/api-utils";
import { BikeATStore, IBikeStore, BikeModel, getBikeModelAndSubscriptions } from "/lib/bike";
import {
  ISubscriptionStore,
  SubscriptionATStore,
  filterBikeSubscriptions
} from "/lib/subscription";

type BikeOrderProps = {
  page: {
    title: string;
    intro: string;
    content: any[];
  };
  slug: string;
  bikeModel: BikeItemContent;
  customerHasValidCreditCard: boolean;
};

export type Subscription = {
  id: string;
  title: string;
  price: number;
  commitmentTime: string;
  description: string;
};

// Function to Base64 decode a string
function decodeBase64(encoded: string) {
  return Buffer.from(encoded, "base64").toString("utf-8");
}

function BikeOrder({ page, bikeModel, slug, customerHasValidCreditCard }: BikeOrderProps) {
  const { content = [] } = page || {};

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);

    trackPixel("ViewContent", {
      content_category: "order",
      content_name: bikeModel?.cms?.title
    });
  }, [bikeModel?.cms?.title]);

  return (
    <>
      <Seo isFrontpage={false} page={page} />

      <article>
        <Section limitedWidth outer="firstSection">
          <Block bottom={7} mobileTop={4}>
            <h1>Bestill Whee! sykkelabonnement</h1>
          </Block>

          <Stepper bikeModel={bikeModel} customerHasValidCreditCard={customerHasValidCreditCard} />
        </Section>

        <Block>
          {content.map((item: any) => (
            <CmsBlock data={item} key={item._key} />
          ))}
        </Block>
      </article>

      <Footer slug={slug} />
    </>
  );
}

export const getServerSideProps = async (ctx: {
  req: NextApiRequest;
  query: NextApiRequestQuery;
}) => {
  const bikeStore: IBikeStore = new BikeATStore();
  const subscriptionStore: ISubscriptionStore = new SubscriptionATStore();

  try {
    const cookie = ctx.req.cookies;
    let customerHasValidCreditCard = false;
    const customer = {};
    const bikeModelId = ctx.query.bikeId as string | undefined;

    if (!bikeModelId) {
      // No bikeId provided, redirect back to overview page
      console.log("Redirecting from /sykler/bestill/[bikeId]");
      return {
        redirect: {
          destination: "/sykler",
          permanent: false
        }
      };
    }

    if (cookie?.uid) {
      console.log("Found uid: ", cookie.uid);
      // Check if existing customer has a valid credit card
      const paymentMethods = await stripe.paymentMethods
        .list({
          customer: cookie.uid,
          type: "card",
          limit: 1
        })
        .catch(() => null);

      if (paymentMethods?.data[0]?.card?.exp_month) {
        const cardExpireMonth = paymentMethods?.data[0]?.card?.exp_month;
        const cardExpireYear = paymentMethods?.data[0]?.card?.exp_year;
        customerHasValidCreditCard = new Date(cardExpireYear, cardExpireMonth) > new Date();
      }
    }

    const bikeModelQuery = createBikeModelsQuery().filter(`id == '${bikeModelId}'`);

    const query = bikeModelQuery.query;
    const pageResponse = await sanity.fetch(query);

    const { bikeModel, subscriptions } = await getBikeModelAndSubscriptions(
      bikeStore,
      subscriptionStore,
      bikeModelId
    );

    const bikesAvailableForSale = bikeModel.bikesAvailableForSale > 0;
    const publicSalesAudience = bikeModel.salesAudience === "Public";
    const waitlistSalesAudience = bikeModel.salesAudience === "Waitlist";

    let allowed = false;

    if (publicSalesAudience && bikesAvailableForSale) {
      allowed = true;
    }

    // [TEMPORARY] This is a temporary solution to allow for a phased rollout of the waitlist
    // Encoded (expires) is being phased out in favour of expiresAt to make it easier update links in templates
    // All of this is a semi hack for now, it should all be automated at some point
    const encodedExpiresDateString = decodeBase64((ctx.query?.expires as string) || "");
    const expiresAtDateString = (ctx.query?.expiresAt as string) || "";
    const todayDateString = new Date().toISOString().split("T")[0];
    const encodedHasNotExpired = encodedExpiresDateString >= todayDateString;
    const expiresAtHasNotExpired = expiresAtDateString >= todayDateString;
    const hasNotExpired = encodedHasNotExpired || expiresAtHasNotExpired;

    if (waitlistSalesAudience && bikesAvailableForSale && hasNotExpired) {
      allowed = true;
    }

    if (!allowed) {
      console.log("Redirecting from /sykler/bestill/[bikeId]");
      return {
        redirect: {
          destination: "/sykler",
          permanent: false
        }
      };
    }

    const page: BikeModelsQ = bikeModelsQ.schema.parse(pageResponse);

    if (page.length === 0) {
      throw new Error("No Sanity page data for bike model");
    }

    const supplementedBikeModelData: BikeItemContent = {
      cms: page[0],
      bike: bikeModel,
      bikeSubscriptions: subscriptions
    };

    const props = {
      page,
      bikeModel: supplementedBikeModelData,
      customer,
      customerHasValidCreditCard
    };

    return {
      // HACK: JSON.parse(JSON.stringify(props)) is used to remove undefined values to allow for lacking page data
      props: JSON.parse(JSON.stringify(props))
    };
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
    const message = `Error at page sykler/bestill: ${error?.message}`;
    return handleServerSidePropsError(message);
  }
};

export default BikeOrder;

type SanityBikeModel = BikeModelsQ[0];
export type BikeItemContent = {
  cms: SanityBikeModel;
  bike: BikeModel;
  bikeSubscriptions: ReturnType<typeof filterBikeSubscriptions>;
};
